import { StrictMode, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ThemeProvider, GlobalStyles } from '@mui/material';
import './styles/app.css';
import { themeFor } from './theme/theme';
import { DARK, LIGHT, cssVars } from './theme/tokens';
import { AboutPanel } from './features/about/AboutPanel';
import { SavedToast } from './features/toast/SavedToast';
import { RouteNotice } from './features/routenotice/RouteNotice';
import { MyItems } from './features/myitems/MyItems';
import { HelpModal } from './features/help/HelpModal';
import { PrivacyPanel } from './features/privacy/PrivacyPanel';
import { Dashboard } from './features/dashboard/Dashboard';
import { LoginOverlay } from './features/login/LoginOverlay';
import { TopbarLeft } from './features/topbar/TopbarLeft';
import { TopbarRight } from './features/topbar/TopbarRight';
import { TopbarSearch } from './features/topbar/TopbarSearch';
import { AiDocConfig } from './features/aidoc/AiDocConfig';
import { LiveVerify } from './features/liveverify/LiveVerify';
import { ChatDock } from './features/chat/ChatDock';
import { PermissionsPage } from './features/permissions/PermissionsPage';
import { LogDetail } from './features/doclog/LogDetail';
import { DocPicker } from './features/docpicker/DocPicker';
import { DocOverlay } from './features/docview/DocOverlay';
import { HeatmapLegend } from './features/heatmap/HeatmapLegend';
import { Minimap } from './features/minimap/Minimap';
import { ContextMenu } from './features/contextmenu/ContextMenu';
import { MapStage } from './features/map/MapStage';
import { NodePopup } from './features/popup/NodePopup';
import { TargetsScreen } from './features/targets/TargetsScreen';
import * as MapController from './features/map/mapController';
import { getStoreState } from './store';
import { registerHandle } from './app/handles';
import { pageHandle, useAppState } from './app/state';
import { publishPageScope } from './app/pageScope';
import { boot } from './app/boot';

/**
 * THE ENTRY POINT.
 *
 * `index.html` carries the page's shell — the top bar's three zones, the sidebar, the stage,
 * the AI-doc toolbar, the zoom buttons and the empty hosts every full-screen surface mounts
 * into — and this file fills them in and starts the app.
 *
 * ── WHY MANY ROOTS AND NOT ONE ───────────────────────────────────────────────────────────
 * Each region is its own `createRoot` over a host element that already exists in the markup.
 * That is the arrangement the whole migration was built and pixel-tested against: a region
 * renders the classes the stylesheet already had, into the box it already had, and the
 * baselines never moved. Collapsing it into a single tree would mean re-expressing the shell
 * as JSX — a second change, at the step with the least margin for one. The regions are
 * independent by construction (they share state through `app/state.ts`, never through props),
 * so nothing is gained by making them siblings in one tree either.
 *
 * ── ORDER ────────────────────────────────────────────────────────────────────────────────
 * Regions mount BEFORE `boot()` runs, so that the handles they register (`popup.open`,
 * `docPicker.open`, the map's layout queue) exist by the time boot's first `render()` reaches
 * them. Boot is async and its first await is a network call, so a region's own effects have
 * run long before any of it matters; mounting first removes the question entirely.
 */

type Region = {
  /** Element id in index.html whose contents this region owns. */
  hostId: string;
  render: (host: HTMLElement) => ReactNode;
};

const REGIONS: Region[] = [
  // The About panel. Its trigger is the version line in the brand, which is the top bar's.
  { hostId: 'about', render: (host) => <AboutPanel host={host} /> },

  // The "Saved" toast. Every save path ends here.
  { hostId: 'saved', render: (host) => <SavedToast host={host} /> },

  // The unreachable-link notice (plan 1.6): a shared link pointed at something this user
  // cannot see, or that no longer exists. Empty until the router says so.
  { hostId: 'routenotice', render: (host) => <RouteNotice host={host} /> },

  // "My items" — this user's favorites and recently-viewed docs (plan 2.4). The sidebar's
  // only occupant: plan 2.3 moved the tag tree into the topbar's tags dropdown, where it
  // renders as a CHILD of the TopbarSearch region rather than a mount of its own.
  { hostId: 'my-items', render: () => <MyItems /> },

  // The help guide. Empty until opened, where the eleven modals used to sit permanently in
  // the DOM.
  { hostId: 'helpmodal', render: (host) => <HelpModal host={host} /> },

  // The privacy page. Openable pre-login (from the sign-in card) as well as from About,
  // which is why its host outranks #loginmodal in the stylesheet.
  { hostId: 'privacymodal', render: (host) => <PrivacyPanel host={host} /> },

  // The coverage dashboard.
  { hostId: 'dashboardmodal', render: (host) => <Dashboard host={host} /> },

  // The sign-in overlay. Its visibility is DERIVED (`booted && !currentUser`) rather than
  // commanded, so a `showLogin()` that arrives before this mounts cannot be lost — and an
  // already-signed-in user never sees the card flash while the session check is in flight.
  { hostId: 'loginmodal', render: (host) => <LoginOverlay host={host} /> },

  // The top bar, in three zones. The fit controller (features/topbar/topbarFit.ts) measures
  // the zones rather than caring who rendered them, which is what made splitting it safe.
  { hostId: 'tb-right', render: (host) => <TopbarRight host={host} /> },
  { hostId: 'tb-left', render: (host) => <TopbarLeft host={host} /> },
  { hostId: 'tb-center', render: (host) => <TopbarSearch host={host} /> },

  // The AI documentation suite.
  { hostId: 'aidoccfgmodal', render: (host) => <AiDocConfig host={host} /> },
  { hostId: 'targetsmodal', render: (host) => <TargetsScreen host={host} /> },
  { hostId: 'liveverifymodal', render: (host) => <LiveVerify host={host} /> },

  // The documentation chatbot.
  { hostId: 'chatdock', render: (host) => <ChatDock host={host} /> },

  // Users, groups and permissions.
  { hostId: 'permmodal', render: (host) => <PermissionsPage host={host} /> },

  // One log entry in full, opened by id from two places that know nothing about each other:
  // the Document logs table and the per-document timeline.
  { hostId: 'logdetail', render: (host) => <LogDetail host={host} /> },

  // Choosing, creating or detaching the documentation a node points at.
  { hostId: 'docpickmodal', render: (host) => <DocPicker host={host} /> },

  // The documentation overlay: the bar, the navigation band, the page in each of its three
  // shapes, and the Tiptap editor. The per-document version timeline is a cell of its grid.
  { hostId: 'docmodal', render: (host) => <DocOverlay host={host} /> },

  // The clearance heatmap's legend (V0.39). A sibling of #stage, so a click on it can never
  // start a map pan; empty until the mode is on.
  { hostId: 'heatmap-legend', render: (host) => <HeatmapLegend host={host} /> },

  // The minimap (V0.53.4). A sibling of #stage for the legend's reason; empty while its toggle —
  // the first row of the zoom stack — is off.
  { hostId: 'minimap', render: (host) => <Minimap host={host} /> },

  // The map. Its host is #maproot rather than #world, because the region renders BOTH the wire
  // <svg> and the world <div>: two roots would need the camera applied twice and could not
  // share one pointer handler.
  { hostId: 'maproot', render: (host) => <MapStage host={host} /> },

  // The node popup. Its own root rather than part of the map, because it is a modal OVER the
  // map: nothing in the map's render path reads it, and its host is a sibling of #maproot. It
  // also drives #scrim, which stays plain markup because a root owns only what is inside its
  // host.
  { hostId: 'popup', render: (host) => <NodePopup host={host} /> },

  // The right-click menu (V0.54). Its own root at the top level of the page: it opens over the
  // map, the minimap and the popup alike, and a card inside any of their hosts would inherit
  // their stacking and, under a backdrop-filter, their containing block.
  { hostId: 'ctxmenu', render: (host) => <ContextMenu host={host} /> },
];

/**
 * Theme and MUI.
 *
 * `GlobalStyles` is deliberately NOT injecting the custom properties: `styles/app.css` defines
 * them once, in `:root` and `.light`, which is what makes the theme switch instant and what
 * every region's own CSS reads. MUI is themed FROM the same tokens (`theme/tokens.ts`) so the
 * two cannot disagree — see the test that holds them together.
 *
 * `CssBaseline` is deliberately absent for the same reason it always was: it would restyle the
 * page around the regions.
 */
function Providers({ children }: { children: ReactNode }) {
  const light = useAppState().isLight;
  return (
    <ThemeProvider theme={themeFor(light)}>
      <GlobalStyles styles={{ ':root': cssVars(light ? LIGHT : DARK) }} />
      {children}
    </ThemeProvider>
  );
}

const roots = new Map<string, Root>();

function mount(region: Region): void {
  const host = document.getElementById(region.hostId);
  if (!host) {
    console.warn(`[app] host #${region.hostId} is not in the page; region not mounted`);
    return;
  }
  let root = roots.get(region.hostId);
  if (!root) {
    root = createRoot(host);
    roots.set(region.hostId, root);
  }
  root.render(
    <StrictMode>
      <Providers>{region.render(host)}</Providers>
    </StrictMode>,
  );
}

/** Render every region into its host. */
export function mountAll(): void {
  for (const region of REGIONS) mount(region);
}

// The map's imperative surface — layout, the camera, the in-place rename — registered here
// rather than from inside the region, because it is module-level state the region drives and
// not something a component owns.
registerHandle('map', {
  relayout: (intent) => MapController.requestLayout(intent),
  syncDocsExpand: () => MapController.requestDocsSync(),
  settle: (anchorId, intent) => MapController.requestSettle(anchorId ?? null, intent),
  fit: (opts) => MapController.requestFit(opts),
  centerOnNodes: (ids, opts) => MapController.requestCenter(ids ?? [], opts),
  zoomBy: MapController.zoomBy,
  getCamera: MapController.getCamera,
  setCamera: (v, opts) => MapController.setCamera(v, opts),
  beginRename: (id) => getStoreState().setRenamingId(id),
});

pageHandle.mountAll = mountAll;
publishPageScope();
mountAll();
void boot();
